import React from 'react';
import {TextColor, TextPosition} from '../text-modifiers';
import {Link} from '../Link.jsx';
import {TrackingContextAppend} from '../TrackingContext.jsx';
import {ProductSummary} from '../ProductSummary.jsx';
import {Video} from '../Video.jsx';
import {Picture} from '../Picture.jsx';
import {XRay} from '@teaser/scripts/components/XRay.jsx';
import {CfEntry} from '@teaser/scripts/components/CfPreviewContext.jsx';

/**
 * @typedef {TeaserLinkModel} OutfitInspirationSingleTeaserModel
 * @property {string} entryId
 * @property {string} headline
 * @property {string} backgroundColor
 * @property {ImageModel | null} backgroundImage
 * @property {VideoModel | null} video
 * @property {ProductSummaryModel[]} products
 */

/**
 * @typedef {TeaserLinkModel} OutfitInspiration
 * @property {string} entryId
 * @property {string|null} headline
 * @property {string|null} subline
 * @property {TextColor|null} headerColor
 * @property {TextPosition} headlineAndSublineAlignment
 * @property {string} labelBackgroundColor
 * @property {TextColor} labelTextColor
 * @property {TextPosition} labelAlignment
 * @property {string} backgroundColor
 * @property {boolean} showProductInfo
 * @property {string} contentType
 * @property {OutfitInspirationSingleTeaserModel[]} teasers


 * @property {string|null} callToAction
 * @property {'PRODUCT_SLIDER'} type
 * @property {any[]} products
 * @property {TextColor} textColor
 */

/**
 * @param {OutfitInspiration} model
 * @param {Device} device
 * @returns {JSX.Element}
 */
export function OutfitInspiration({model, device}) {
    const isMobile =
        device.name === 'mobile' || device.name === 'app' || device.name === 'unknown' || device.name === undefined;

    return (
        <CfEntry entryId={model.entryId} contentType={model.contentType}>
            <article
                className="content-platform__teaser content-platform__outfit-teaser"
                style={{
                    backgroundColor: model.backgroundColor,
                }}
            >
                <XRay fieldId="internalName" />
                {(model.headline || model.subline) && (
                    <div
                        className={`content-platform__outfit-teaser__header content-platform__topic-carousel__header ${TextPosition[model.headlineAndSublineAlignment]}`}
                    >
                        {model.headline && (
                            <p
                                className={'content-platform__topic-carousel__headline'}
                                style={{color: model.headerColor}}
                            >
                                <XRay fieldId="headline" />
                                {model.headline}
                            </p>
                        )}
                        {model.subline && (
                            <p
                                className={'content-platform__topic-carousel__subline'}
                                style={{color: model.headerColor}}
                            >
                                {model.subline}
                                <XRay fieldId="subline" />
                            </p>
                        )}
                    </div>
                )}
                <div className={'content-platform__outfit-teaser__content'}>
                    {model.teasers.map(
                        (teaser) =>
                            teaser &&
                            teaser.entryId && (
                                <OutfitInspirationItem key={teaser.entryId} model={teaser} parent={model} />
                            ),
                    )}
                </div>
            </article>
        </CfEntry>
    );

    function OutfitInspirationItem({model, parent}) {
        return (
            <CfEntry entryId={model.entryId} contentType={model.contentType}>
                <TrackingContextAppend value={{id: model.entryId}}>
                    <div className={'content-platform__outfit-teaser__single-teaser'}>
                        <XRay fieldId="internalName" />
                        {model.linkUrlBasePath ? (
                            <Link
                                key={model.entryId}
                                model={model}
                                className={`content-platform__outfit-teaser__single-outfit`}
                            >
                                <SingleOutfitTeaser parent={parent} model={model} />
                            </Link>
                        ) : (
                            <div className={`content-platform__outfit-teaser__single-outfit`}>
                                <SingleOutfitTeaser parent={parent} model={model} />
                            </div>
                        )}

                        <ProductsForSingleOutfit products={model.products} showProductInfo={parent.showProductInfo} />
                    </div>
                </TrackingContextAppend>
            </CfEntry>
        );
    }

    function SingleOutfitTeaser({model, parent}) {
        return (
            <>
                {' '}
                <Video model={model} aspectRatio={0.714}>
                    {model.backgroundImage ? (
                        <Picture
                            model={model.backgroundImage}
                            xxlarge={620}
                            xlarge={512}
                            large={350}
                            medium={575}
                            small={359}
                            aspectRatio={0.714}
                        />
                    ) : (
                        <div
                            className="content-platform__teaser__spacer content-platform__outfit-teaser__spacer"
                            style={{backgroundColor: model.backgroundColor}}
                        />
                    )}
                </Video>
                {model.headline && (
                    <div className="content-platform__teaser__content">
                        <div
                            className={`content-platform__topic-carousel__slide-main 
                                                ${TextPosition[parent.labelAlignment]}`}
                        >
                            <p
                                className={`content-platform__topic-carousel__slide-headline 
                                                ${TextColor[parent.labelTextColor]} 
                                                -bg${TextColor[parent.labelBackgroundColor]}`}
                            >
                                <XRay fieldId="headline" />
                                {model.headline}
                            </p>
                        </div>
                    </div>
                )}
            </>
        );
    }

    function ProductsForSingleOutfit({products, showProductInfo}) {
        if (!products) return <div className="content-platform__outfit-teaser__spacer"></div>;

        return (
            <div className="content-platform__outfit-teaser__products">
                {products.map((product) => {
                    return (
                        <ProductSummary
                            product={product}
                            className="content-platform__outfit-teaser__single-product"
                            showProductInfo={showProductInfo}
                            key={product.entryId}
                        />
                    );
                })}
            </div>
        );
    }
}
